/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';
import type { GetTipoCompetenzaSecondariaParams } from '../schemas/getTipoCompetenzaSecondariaParams.ts';
import type { TipoCompetenzaSecondariaDTO } from '../schemas/tipoCompetenzaSecondariaDTO.ts';
import type { TipoCompetenzaSecondariaRequest } from '../schemas/tipoCompetenzaSecondariaRequest.ts';
import type { WsRispostaPaginataListTipoCompetenzaSecondariaDTO } from '../schemas/wsRispostaPaginataListTipoCompetenzaSecondariaDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce un tipo di competenza secondaria a partire dal suo id
 * @summary Dettaglio tipo competenza secondaria
 */
export const getTipoCompetenzaSecondariaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<TipoCompetenzaSecondariaDTO>(
    {
      url: `/framework-cfg-utenti/tipo-competenza-secondaria/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetTipoCompetenzaSecondariaIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/tipo-competenza-secondaria/${id}`] as const;

export const getGetTipoCompetenzaSecondariaIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipoCompetenzaSecondariaIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>
  > = ({ signal }) => getTipoCompetenzaSecondariaId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetTipoCompetenzaSecondariaIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>
>;
export type GetTipoCompetenzaSecondariaIdQueryError = ErrorType<unknown>;

/**
 * @summary Dettaglio tipo competenza secondaria
 */
export const useGetTipoCompetenzaSecondariaId = <
  TData = Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipoCompetenzaSecondariaId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipoCompetenzaSecondariaIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Modifica un tipo di competenza secondaria
 * @summary Aggiorna tipo competenza secondaria
 */
export const putTipoCompetenzaSecondariaId = (
  id: string,
  tipoCompetenzaSecondariaRequest: TipoCompetenzaSecondariaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoCompetenzaSecondariaDTO>(
    {
      url: `/framework-cfg-utenti/tipo-competenza-secondaria/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: tipoCompetenzaSecondariaRequest,
    },
    options,
  );
};

export const getPutTipoCompetenzaSecondariaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipoCompetenzaSecondariaId>>,
    TError,
    { id: string; data: TipoCompetenzaSecondariaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putTipoCompetenzaSecondariaId>>,
  TError,
  { id: string; data: TipoCompetenzaSecondariaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putTipoCompetenzaSecondariaId>>,
    { id: string; data: TipoCompetenzaSecondariaRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putTipoCompetenzaSecondariaId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTipoCompetenzaSecondariaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putTipoCompetenzaSecondariaId>>
>;
export type PutTipoCompetenzaSecondariaIdMutationBody =
  TipoCompetenzaSecondariaRequest;
export type PutTipoCompetenzaSecondariaIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna tipo competenza secondaria
 */
export const usePutTipoCompetenzaSecondariaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putTipoCompetenzaSecondariaId>>,
    TError,
    { id: string; data: TipoCompetenzaSecondariaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutTipoCompetenzaSecondariaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove un tipo di competenza secondaria
 * @summary Elimina tipo competenza secondaria
 */
export const deleteTipoCompetenzaSecondariaId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/tipo-competenza-secondaria/${id}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteTipoCompetenzaSecondariaIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipoCompetenzaSecondariaId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTipoCompetenzaSecondariaId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTipoCompetenzaSecondariaId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteTipoCompetenzaSecondariaId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTipoCompetenzaSecondariaIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTipoCompetenzaSecondariaId>>
>;

export type DeleteTipoCompetenzaSecondariaIdMutationError = ErrorType<unknown>;

/**
 * @summary Elimina tipo competenza secondaria
 */
export const useDeleteTipoCompetenzaSecondariaId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTipoCompetenzaSecondariaId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getDeleteTipoCompetenzaSecondariaIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ricerca paginata dei tipi di competenza secondaria
 * @summary Ricerca tipo competenza secondaria
 */
export const getTipoCompetenzaSecondaria = (
  params?: GetTipoCompetenzaSecondariaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListTipoCompetenzaSecondariaDTO>(
    {
      url: `/framework-cfg-utenti/tipo-competenza-secondaria`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetTipoCompetenzaSecondariaQueryKey = (
  params?: GetTipoCompetenzaSecondariaParams,
) =>
  [
    `/framework-cfg-utenti/tipo-competenza-secondaria`,
    ...(params ? [params] : []),
  ] as const;

export const getGetTipoCompetenzaSecondariaQueryOptions = <
  TData = Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipoCompetenzaSecondariaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTipoCompetenzaSecondariaQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>
  > = ({ signal }) =>
    getTipoCompetenzaSecondaria(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetTipoCompetenzaSecondariaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>
>;
export type GetTipoCompetenzaSecondariaQueryError = ErrorType<unknown>;

/**
 * @summary Ricerca tipo competenza secondaria
 */
export const useGetTipoCompetenzaSecondaria = <
  TData = Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>,
  TError = ErrorType<unknown>,
>(
  params?: GetTipoCompetenzaSecondariaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTipoCompetenzaSecondaria>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetTipoCompetenzaSecondariaQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Inserisce un nuovo tipo di competenza secondaria
 * @summary Crea tipo competenza secondaria
 */
export const postTipoCompetenzaSecondaria = (
  tipoCompetenzaSecondariaRequest: TipoCompetenzaSecondariaRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<TipoCompetenzaSecondariaDTO>(
    {
      url: `/framework-cfg-utenti/tipo-competenza-secondaria`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: tipoCompetenzaSecondariaRequest,
    },
    options,
  );
};

export const getPostTipoCompetenzaSecondariaMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipoCompetenzaSecondaria>>,
    TError,
    { data: TipoCompetenzaSecondariaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postTipoCompetenzaSecondaria>>,
  TError,
  { data: TipoCompetenzaSecondariaRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postTipoCompetenzaSecondaria>>,
    { data: TipoCompetenzaSecondariaRequest }
  > = props => {
    const { data } = props ?? {};

    return postTipoCompetenzaSecondaria(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostTipoCompetenzaSecondariaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postTipoCompetenzaSecondaria>>
>;
export type PostTipoCompetenzaSecondariaMutationBody =
  TipoCompetenzaSecondariaRequest;
export type PostTipoCompetenzaSecondariaMutationError = ErrorType<unknown>;

/**
 * @summary Crea tipo competenza secondaria
 */
export const usePostTipoCompetenzaSecondaria = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postTipoCompetenzaSecondaria>>,
    TError,
    { data: TipoCompetenzaSecondariaRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostTipoCompetenzaSecondariaMutationOptions(options);

  return useMutation(mutationOptions);
};
